import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'antd';
import classNames from 'classnames';

import {
  exchangesSelector,
  getGivenExchangesLabels,
  getWeGoldExchanges,
  givenFiltersSelector,
  receiveExchangesSelector,
} from 'src/store/exchanges';

import { CustomButton, CustomInput, WhiteCard } from 'src/components';

import { REACT_APP_API_URL } from 'src/constants';

import { ReactComponent as SearchIcon } from 'src/assets/exchange/SearchIcon.svg';
import './exchangeWeGold.scss';
import { LetterIcon, TelegramIcon } from 'src/assets';

export const ExchangeWeGold = () => {
  const { t, i18n } = useTranslation('exchange');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const params = useParams();

  const id = params?.id;

  const exchanges = useSelector(exchangesSelector);

  const givenFilters = useSelector(givenFiltersSelector);
  const selectedReceive = useSelector(receiveExchangesSelector);

  const [selectedGive, setSelectedGive] = useState({});

  const [selectedGiveFilter, setSelectedGiveFilter] = useState('');

  const [givenData, setGivenData] = useState([]);

  const [wegold, setWegold] = useState('');

  useEffect(() => {
    dispatch(getWeGoldExchanges({ id }));
    dispatch(getGivenExchangesLabels());
    setWegold(id);
  }, [dispatch, i18n.language, id]);

  useEffect(() => {
    setGivenData(exchanges || []);

    setSelectedGive(exchanges?.[0] || {});
  }, [exchanges]);

  const onClickGiveItem = useCallback(
    (item) => () => {
      setSelectedGive(item);
    },
    [],
  );

  const onClickContinue = useCallback(() => {
    navigate(`/exchange/${selectedGive?.exchange_id}/create?we_gold_id=${wegold}`);
  }, [navigate, selectedGive?.exchange_id, wegold]);

  const onSelectGivenFilter = useCallback(
    (filter) => () => {
      setSelectedGiveFilter(filter);

      const filteredList = exchanges?.filter((item) =>
        item?.label?.toLowerCase()?.includes(filter?.toLowerCase()),
      );

      setGivenData(filteredList);
    },
    [exchanges],
  );

  const onChangeGivenSearch = useCallback(
    (event) => {
      const value = event.target.value;

      const filteredList = exchanges?.filter(
        (item) =>
          item?.name?.toLowerCase()?.includes(value?.toLowerCase()) ||
          item?.code?.toLowerCase()?.includes(value?.toLowerCase()),
      );

      setGivenData(filteredList);
    },
    [exchanges],
  );

  const onChangeWeGold = useCallback((event) => {
    setWegold(event.target.value);
  }, []);

  const renderSection = useCallback(
    (props) => {
      const {
        title,
        filter,
        filters,
        onClick = () => {},
        onChangeSearch = () => {},
        onSelectFilter = () => {},
        data = [],
        id,
      } = props;

      return (
        <div className="exchange-we-gold__section">
          <div className="exchange-we-gold__section__title">
            <h3>{title}</h3>
            <div className="exchange-we-gold__section__filters">
              {filters.map((item) => {
                const buttonStyles = classNames('exchange-we-gold__section__button', {
                  'exchange-we-gold__section__button--active': filter === item.name,
                });

                return (
                  <Button
                    color="default"
                    variant="filled"
                    className={buttonStyles}
                    key={item.name}
                    onClick={onSelectFilter(item?.name)}
                  >
                    {item.translate}
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="exchange-we-gold__section__search">
            <CustomInput
              placeholder={t('search')}
              suffix={<SearchIcon />}
              onChange={onChangeSearch}
            />
          </div>
          <div className="exchange-we-gold__section__list">
            {data.map((item, index) => (
              <div
                className={`exchange-we-gold__section__item ${
                  item?.id === id ? 'exchange-we-gold__section__item--selected' : ''
                }`}
                key={item.id}
                onClick={onClick(item)}
              >
                <div className="exchange-we-gold__section__item__image">
                  <img
                    src={REACT_APP_API_URL + item?.image}
                    alt={REACT_APP_API_URL + item?.image}
                  />
                </div>
                <div>
                  {item?.name} <span>({item?.code})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    },
    [t],
  );

  return (
    <div className="exchange-we-gold__wrapper">
      <div className="exchange-we-gold">
        {renderSection({
          title: t('give'),
          filter: selectedGiveFilter,
          filters: givenFilters,
          onClick: onClickGiveItem,
          onChangeSearch: onChangeGivenSearch,
          onSelectFilter: onSelectGivenFilter,
          data: givenData,
          id: selectedGive?.id,
        })}

        <div className="exchange-we-gold__section">
          <div className="exchange-we-gold__section__title">
            <h3>{t('receive')}</h3>
          </div>
          {exchanges?.length ? (
            <div className="exchange-we-gold__section__list" style={{ gap: 30 }}>
              <div className="exchange-we-gold__section__list__left">
                <div
                  className={`exchange-we-gold__section__item exchange-we-gold__section__item--selected`}
                >
                  <div className="exchange-we-gold__section__item__image">
                    <img
                      src={REACT_APP_API_URL + selectedReceive?.image}
                      alt={REACT_APP_API_URL + selectedReceive?.image}
                    />
                  </div>
                  <div>{selectedReceive?.name}</div>
                </div>
                <CustomInput
                  label={`${t('wegoldLabel')} ${selectedReceive?.name}`}
                  value={wegold}
                  onChange={onChangeWeGold}
                />
                <CustomButton
                  title={t('title:continue')}
                  variant="filled"
                  onClick={onClickContinue}
                />
              </div>
              <div className="exchange-we-gold__section__list__right">
                <ol>
                  <li>{t('li1')}</li>
                  <li>{t('li2')}</li>
                </ol>
                <div className="exchange-we-gold__section__list__right__links">
                  <Link to="mailto:support@exok.cash">
                    <img src={LetterIcon} alt={LetterIcon} />
                    support@exok.cash
                  </Link>
                  <Link to="tg://resolve?domain=exok_manager" target="_blank">
                    <img src={TelegramIcon} alt={TelegramIcon} />
                    Telegram ExOk
                  </Link>
                  <Link to="https://web.telegram.org/k/" target="_blank">
                    <img src={TelegramIcon} alt={TelegramIcon} />
                    Telegram WeGold
                  </Link>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="create-exchange__instructions">
        <WhiteCard>
          <h2>
            {t('createExchange:instructionsExchange')} {t('createExchange:to')}{' '}
            {selectedReceive?.name}
          </h2>
          <h4>{t('createExchange:forExchange')}</h4>
          <ol>
            <li>{t('createExchange:li1')}</li>
            <li>{t('createExchange:li2')}</li>
            <li>{t('createExchange:li3')}</li>
            <li>{t('createExchange:li4')}</li>
          </ol>
          <div className="create-exchange__instructions__content">
            {t('createExchange:instructionsContent')}
          </div>
        </WhiteCard>
      </div>
    </div>
  );
};
